<template>
  <authenticated-layout
    :back-route="{ name: 'cohorts' }"
    :breadcrumbs="breadcrumbItems">
    <PageHeader :title="cohort?.title">
      <template #actions>
        <v-menu
          bottom
          transition="scale-transition"
          v-if="store.user?.app.role !== Role.Manager"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              color="primary"
              prepend-icon="fa fa-cog"
              data-pw="menuButton"
            >
              Settings
            </v-btn>
          </template>
          <v-list min-width="250px">
            <v-list-item
              :disabled="!isAdminOrRoot"
              @click="showResetAssessmentsDialog = true"
            >
              {{ $t("components.CohortDetailPage.menu.reset_assessments") }}
            </v-list-item>
            <v-list-item
              v-if="isAdminOrRoot"
              :disabled="!isAdminOrRoot && !store.hasAbility(Entity.Cohort, Action.edit)"
              @click="showEditCohort = true"
            >
              <span>Edit</span>
            </v-list-item>
            <v-list-item
              v-if="isAdminOrRoot"
              :disabled="!isAdminOrRoot"
              @click="showMigrateCohortData = true"
            >
              <span class="text-orange">Migrate Data</span>
            </v-list-item>
            <v-list-item
              v-if="isAdminOrRoot"
              :disabled="!isAdminOrRoot"
              @click="showMoveCohort = true"
            >
              <span class="text-red">Move</span>
            </v-list-item>
            <v-list-item
              v-if="isAdminOrRoot"
              :disabled="!isAdminOrRoot && !store.hasAbility(Entity.Cohort, Action.delete)"
              @click="showDeleteCohort = true"
            >
              <span class="text-red">{{ $t("buttons.delete") }}</span>
            </v-list-item>
          </v-list>
        </v-menu>

      </template>
    </PageHeader>

    <v-container class="mt-0 mx-auto pa-0 border-b mb-6" fluid>
      <v-tabs v-model="activeTab" grow>
        <v-tab value="Dashboard">
          Overview
        </v-tab>
      </v-tabs>
    </v-container>

    <v-container v-if="cohort" class="py-4" fluid>
      <v-row v-if="activeTab === 'Dashboard'">
        <v-col cols="9">
          <h1>Insights coming soon.</h1>
        </v-col>
        <v-col cols="3">
          <cohort-details :cohort="cohort" />
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="showResetAssessmentsDialog"  max-width="500">
      <reset-assessment-dialog
        :id="cohort?._id ?? ''"
        type="cohort"
        @close="onCloseResetAssessmentDialog"
      />
    </v-dialog>

    <v-dialog v-model="showEditCohort" max-width="500">
      <edit-cohort
        @close="onEditDialog()"
        :cohort="cohort!"/>
    </v-dialog>

    <v-dialog v-model="showMigrateCohortData" max-width="500">
      <migrate-cohort
        v-if="cohort"
        @close="showMigrateCohortData = false"
        :cohort="cohort"
      />
    </v-dialog>

    <v-dialog v-model="showMoveCohort" max-width="500">
      <move-cohort
        v-if="cohort"
        @close="showMoveCohort = false"
        :cohort="cohort"
      />
    </v-dialog>

    <v-dialog v-model="showDeleteCohort" max-width="500">
      <delete-cohort
        @close="showDeleteCohort = false"
        :confirm-string="cohort?.title ?? ''"
        :message="$t('components.CohortDetailPage.delete_message')"
      />
    </v-dialog>
  </authenticated-layout>
</template>

<script setup lang="ts">
import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";
import {computed, onMounted, ref, watch} from "vue";
import {Action, Cohort, Entity, isYapiError, Role, User, YapiError} from "@YenzaCT/sdk";
import {useRoute} from "vue-router";
import yapi from "@/lib/yapi";
import CohortDetails from "@/pages/Manage/CohortDetailPage/components/CohortDetails.vue";
import DeleteCohort from "@/pages/Manage/CohortDetailPage/components/DeleteCohort.vue";
import {useGlobalStore} from "@/store";
import ResetAssessmentDialog from "@/components/ResetAssessmentCard.vue";
import EditCohort from "@/pages/Manage/CohortDetailPage/components/EditCohort.vue";
import PageHeader from "@/components/PageHeader.vue";
import MoveCohort from "@/pages/Manage/CohortDetailPage/components/MoveCohort.vue";
import MigrateCohort from "@/pages/Manage/CohortDetailPage/components/MigrateCohort.vue";
const store = useGlobalStore();
const cohort = ref<Cohort>();
const managers = ref<User[]>([]);
const route = useRoute();

const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);
const showDeleteCohort = ref(false);
const showMigrateCohortData = ref(false);
const showMoveCohort = ref(false);
const showResetAssessmentsDialog = ref(false);
const showEditCohort = ref(false);
const activeTab = ref("Dashboard");

const breadcrumbItems = computed(() => [
  { text: "Cohorts", to: { name: "cohorts" } },
  { text: cohort.value?.title || "" }
]);

async function fetchCohort() {
  try {
    store.networkBusy = true;
    const cohortId = route.params.cohortId as string;
    cohort.value = (await yapi.admin.cohort.get(cohortId)).data;

    if (isAdminOrRoot.value)
      managers.value = (await yapi.admin.managers.list(Entity.Cohort, cohortId)).data;
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
  }
}

watch(() => route.params.id, async (newId, oldId) => {
  if (newId !== oldId) {
    await fetchCohort();
  }
});

async function onEditDialog() {
  showEditCohort.value = false;
  await fetchCohort();
}

const onCloseResetAssessmentDialog = async () => {
  showResetAssessmentsDialog.value = false;
};

onMounted(async () => {
  await fetchCohort();
});

</script>
